import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import './App.css';
import SessionBootstrap from './components/SessionBootstrap';
import ReferralCapture from './components/ReferralCapture';
import SiteMotion from './components/SiteMotion';
import PageMeta from './components/PageMeta';
import BackToTop from './components/BackToTop';
import NotFoundPage from './pages/NotFoundPage';
import { PUBLIC_TRACKED_PAGES } from './utils/publicPages';
import MemberPhotoGuard from './components/MemberPhotoGuard';
import DiagnosticBanner from './components/DiagnosticBanner';
import useFeatures from './hooks/useFeatures';

// Eager pages — the ad-acquisition surfaces (/register is where paid traffic
// lands, / and /login close behind). Everything else is code-split below so the
// initial bundle a mobile in-app browser must download before first paint stays
// small; each route's chunk loads on first visit.
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import AuthCallback from './pages/AuthCallback';

// Lazy route chunks. lazyNamed() adapts modules that use named exports —
// React.lazy only understands default exports.
const lazyNamed = (loader, name) => lazy(() => loader().then((m) => ({ default: m[name] })));

const DiagnosticStart = lazy(() => import('./pages/DiagnosticStart'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ProfileOnboarding = lazy(() => import('./pages/ProfileOnboarding'));
const PsychometricOnboardingV1 = lazy(() => import('./pages/PsychometricOnboardingV1'));
const PortraitInvite = lazy(() => import('./pages/PortraitInvite'));
const BoostPage = lazy(() => import('./pages/BoostPage'));
const ReferPage = lazy(() => import('./pages/ReferPage'));
const DeepQuestionnaireFlow = lazy(() => import('./pages/DeepQuestionnaireFlow'));
const DemoDeepReport = lazy(() => import('./pages/DemoDeepReport'));
// Design lab — alternate designs under founder review, admin session only.
const LabDashboard = lazy(() => import('./pages/lab/LabDashboard'));

// Core app — Step 1
const MatchesPage = lazy(() => import('./pages/MatchesPage'));
const InterestsPage = lazy(() => import('./pages/InterestsPage'));
const MessagesPage = lazy(() => import('./pages/MessagesPage'));
const ChatPage = lazy(() => import('./pages/ChatPage'));
const ProfileViewPage = lazy(() => import('./pages/ProfileViewPage'));
const CouplePortraitPage = lazy(() => import('./pages/CouplePortraitPage'));

// Auth flows — Issue #2 (Email Verification) & Issue #3 (Password Reset)
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));

// Step 2 (Trust & Conversion)
const PrivacyPolicyPage = lazyNamed(() => import('./pages/LegalPages'), 'PrivacyPolicyPage');
const TermsOfServicePage = lazyNamed(() => import('./pages/LegalPages'), 'TermsOfServicePage');
const AboutUsPage = lazyNamed(() => import('./pages/LegalPages'), 'AboutUsPage');
const CancellationPolicyPage = lazyNamed(() => import('./pages/LegalPages'), 'CancellationPolicyPage');
const DeleteAccountPage = lazyNamed(() => import('./pages/LegalPages'), 'DeleteAccountPage');
const TermsAcceptancePage = lazy(() => import('./pages/TermsAcceptancePage'));

// New public pages
const HowItWorksPage = lazy(() => import('./pages/HowItWorksPage'));
const ConciergePage = lazy(() => import('./pages/ConciergePage'));
const PrivacyConciergeLanding = lazy(() => import('./pages/PrivacyConciergeLanding'));
const ConciergePayPage = lazy(() => import('./pages/ConciergePayPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const EcosystemPage = lazy(() => import('./pages/EcosystemPage'));
const CarePage = lazy(() => import('./pages/CarePage'));
const CareExpertPortal = lazy(() => import('./pages/CareExpertPortal'));
const CareExperts = lazy(() => import('./pages/CareExperts'));
const CareExpertBooking = lazy(() => import('./pages/CareExpertBooking'));
const CareBooking = lazy(() => import('./pages/CareBooking'));
const CareMy = lazy(() => import('./pages/CareMy'));
const MethodologyPage = lazy(() => import('./pages/MethodologyPage'));
const IntentionalMatchmakingPage = lazy(() => import('./pages/IntentionalMatchmakingPage'));
const RelationshipIntelligencePage = lazy(() => import('./pages/RelationshipIntelligencePage'));
const CouplesLandingPage = lazy(() => import('./pages/CouplesLandingPage'));
const CoupleBeginPage = lazy(() => import('./pages/CoupleBeginPage'));
const AllianceMembersPage = lazy(() => import('./pages/AllianceMembersPage'));
const AllianceTermsPage = lazy(() => import('./pages/AllianceTermsPage'));
const QuizPage = lazy(() => import('./pages/QuizPage'));

// Support pages
const HelpCenterPage = lazyNamed(() => import('./pages/SupportPages'), 'HelpCenterPage');
const SafetyTipsPage = lazyNamed(() => import('./pages/SupportPages'), 'SafetyTipsPage');
const ContactUsPage = lazyNamed(() => import('./pages/SupportPages'), 'ContactUsPage');
const BlogListPage = lazy(() => import('./pages/BlogListPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const FAQPage = lazy(() => import('./pages/FAQ'));

// Step 3 (Revenue)
const SubscriptionPage = lazy(() => import('./pages/SubscriptionPage'));
const PaymentSuccessPage = lazy(() => import('./pages/PaymentSuccessPage'));

// Notifications & Unsubscribe
const NotificationPreferencesPage = lazy(() => import('./pages/NotificationPreferencesPage'));
const UnsubscribePage = lazy(() => import('./pages/UnsubscribePage'));

// KYC
const KYCVerificationPage = lazy(() => import('./pages/KYCVerificationPage'));

// CompatibilityIntelligenceReport retained for /deep/report/:pairId (V1 matched pairs)
const CompatibilityIntelligenceReport = lazy(() => import('./pages/CompatibilityIntelligenceReport'));

// New Design V2 — Couple Compatibility Flow (link-based, no login required)
const V2CoupleFlow = lazy(() => import('./pages/V2CoupleFlow'));
const V2CouplePreview = lazyNamed(() => import('./pages/V2CoupleFlow'), 'V2CouplePreview');
// V2 Singles — public personal-report flow (token-based, no login)
const V2SinglesStart = lazy(() => import('./pages/V2SinglesStart'));
// V2 Individual-First Funnel (Solo-First Pivot, Sep 2026) — flag-gated entry
const V2IndividualFlow = lazy(() => import('./pages/V2IndividualFlow'));
const PartnerPortal = lazy(() => import('./pages/PartnerPortal'));
const V2SingleFlow = lazy(() => import('./pages/V2SingleFlow'));
const VerifyReportPage = lazy(() => import('./pages/VerifyReportPage'));
const V2PartnerInvite = lazy(() => import('./pages/V2PartnerInvite'));
const FamilyFormPage = lazy(() => import('./pages/FamilyFormPage'));   // Pillar 2: the parents' form

// V1 Reports — Singles Relationship Intelligence + Couple Compatibility (36Q)
const RelationshipIntelligenceReport = lazy(() => import('./pages/RelationshipIntelligenceReport'));
const MarriageReadinessReportPage = lazy(() => import('./pages/MarriageReadinessReportPage'));
const SampleReportPage = lazy(() => import('./pages/SampleReportPage'));
const CoupleCompatibilityReport = lazy(() => import('./pages/CoupleCompatibilityReport'));
const AssessmentUnlock = lazy(() => import('./pages/AssessmentUnlock'));

// Book store — "21 Secrets to Eternal Love" (flag-gated via BOOK_STORE_ENABLED)
const BookPage = lazy(() => import('./pages/BookPage'));
const BookReaderPage = lazy(() => import('./pages/BookReaderPage'));

// Admin Pages — one shared chunk (all named exports of the same barrel module)
const AdminLogin = lazyNamed(() => import('./pages/admin'), 'AdminLogin');
const AdminLayout = lazyNamed(() => import('./pages/admin'), 'AdminLayout');
const AdminDashboard = lazyNamed(() => import('./pages/admin'), 'AdminDashboard');
const AdminUsers = lazyNamed(() => import('./pages/admin'), 'AdminUsers');
const AdminDemographics = lazyNamed(() => import('./pages/admin'), 'AdminDemographics');
const AdminProfiles = lazyNamed(() => import('./pages/admin'), 'AdminProfiles');
const AdminSubscriptions = lazyNamed(() => import('./pages/admin'), 'AdminSubscriptions');
const AdminDeep = lazyNamed(() => import('./pages/admin'), 'AdminDeep');
const AdminReports = lazyNamed(() => import('./pages/admin'), 'AdminReports');
const AdminAnalytics = lazyNamed(() => import('./pages/admin'), 'AdminAnalytics');
const AdminKYC = lazyNamed(() => import('./pages/admin'), 'AdminKYC');
const AdminFeedback = lazyNamed(() => import('./pages/admin'), 'AdminFeedback');
const AdminModeration = lazyNamed(() => import('./pages/admin'), 'AdminModeration');
const AdminSupport = lazyNamed(() => import('./pages/admin'), 'AdminSupport');
const AdminPromo = lazyNamed(() => import('./pages/admin'), 'AdminPromo');
const AdminCoupleReports = lazyNamed(() => import('./pages/admin'), 'AdminCoupleReports');
const AdminMatchDiagnostics = lazyNamed(() => import('./pages/admin'), 'AdminMatchDiagnostics');
const AdminGrowthFunnel = lazyNamed(() => import('./pages/admin'), 'AdminGrowthFunnel');
const AdminConcierge = lazyNamed(() => import('./pages/admin'), 'AdminConcierge');
const AdminLeads = lazyNamed(() => import('./pages/admin'), 'AdminLeads');
const AdminConciergeClient = lazyNamed(() => import('./pages/admin'), 'AdminConciergeClient');
const AdminBookOrders = lazyNamed(() => import('./pages/admin'), 'AdminBookOrders');
const AdminAccounts = lazyNamed(() => import('./pages/admin'), 'AdminAccounts');
const AdminPricing = lazy(() => import('./pages/admin/AdminPricing'));
const AdminAccessCodes = lazy(() => import('./pages/admin/AdminAccessCodes'));
const AdminPartners = lazy(() => import('./pages/admin/AdminPartners'));
const AdminCare = lazy(() => import('./pages/admin/AdminCare'));
const AdminTeam = lazy(() => import('./pages/admin/AdminTeam'));
const AdminWhatsAppInbox = lazy(() => import('./pages/admin/AdminWhatsAppInbox'));
const AdminBlog = lazy(() => import('./pages/admin/AdminBlog'));
const AdminBlogEditor = lazy(() => import('./pages/admin/AdminBlogEditor'));

// Floating UI — non-critical, loads after the page itself
const InstallPrompt = lazy(() => import('./components/InstallPrompt'));
const SathiWidget = lazy(() => import('./components/SathiWidget'));

// Ad landing pages carry one action (the form), so the chat bubble stays off them.
// The Alliance Member portal is a bureau's private office: no member chat there (Oct 19 2026).
const AD_LANDING_PAGES = ['/privacy-concierge', '/alliance'];
function SathiWidgetGate() {
  const { pathname } = useLocation();
  if (AD_LANDING_PAGES.includes(pathname.replace(/\/+$/, ''))) return null;
  return <SathiWidget />;
}

/**
 * Shown for the brief moment a route chunk is downloading. Matches the app
 * shell (dark navy ground) so navigation never flashes white.
 */
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-8 h-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" aria-label="Loading" />
    </div>
  );
}

/**
 * On every route change:
 *  - If the URL has a hash (e.g. /landing#how-it-works), scroll that section into view.
 *  - If there is no hash, scroll the page back to the very top.
 */
function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      // Give React one tick to finish rendering the target page before querying the DOM.
      const id = hash.replace('#', '');
      const attempt = (tries = 0) => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else if (tries < 10) {
          // Retry up to 10 × 50 ms = 500 ms while the page is still painting.
          setTimeout(() => attempt(tries + 1), 50);
        }
      };
      attempt();
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, [pathname, hash]);

  return null;
}

/**
 * Trackers (GTM/GA4/Pixel/Clarity) only LOAD on public pages — index.html gates
 * that. But a session that starts public and then logs in keeps loaded scripts
 * alive across SPA navigation, so Clarity would keep recording inside the app.
 * This boundary stops the recorder the moment the route leaves the public set.
 * Keep PUBLIC_TRACKED_PAGES in sync with the allowlist in public/index.html.
 */
// PUBLIC_TRACKED_PAGES lives in utils/publicPages.js (shared with SiteMotion).


function TrackingBoundary() {
  const { pathname } = useLocation();
  useEffect(() => {
    const p = pathname.replace(/\/+$/, '') || '/';
    if (!PUBLIC_TRACKED_PAGES.has(p) && typeof window.clarity === 'function') {
      try { window.clarity('stop'); } catch { /* recorder may not have started */ }
    }
  }, [pathname]);
  return null;
}

/**
 * V2 entry gates (Solo-First Pivot, Sep 2026): the bare /v2/couple entry
 * serves the individual-first funnel when V2_INDIVIDUAL_ENABLED is on, and
 * the retired simultaneous couple flow when it is off — the flag IS the
 * revert switch. /v2/individual mirrors it, so with the flag off a direct
 * visitor lands on the live flow instead of a page whose API 404s.
 * Existing couple links (/v2/couple/:linkToken) always keep working.
 */
function V2EntryGate() {
  const { v2_individual, _ready } = useFeatures();
  if (!_ready) return <PageLoader />;
  return v2_individual ? <V2IndividualFlow /> : <V2CoupleFlow />;
}

function V2IndividualGate() {
  const { v2_individual, _ready } = useFeatures();
  if (!_ready) return <PageLoader />;
  return v2_individual ? <V2IndividualFlow /> : <Navigate to="/v2/couple" replace />;
}

function AppRouter() {
  const location = useLocation();

  // Check URL fragment (not query params) for OAuth results:
  // session_id= (legacy Emergent broker) or g_token=/g_pending=/g_error=
  // (direct Google OAuth). This check MUST happen during render, NOT in useEffect
  if (/session_id=|g_token=|g_pending=|g_error=/.test(location.hash || '')) {
    return <AuthCallback />;
  }

  return (
    <>
      <ScrollToTop />
      <TrackingBoundary />
      <MemberPhotoGuard />
      <SiteMotion />
      <PageMeta />
      <BackToTop />
      <Suspense fallback={<PageLoader />}>
      <Routes>
      {/* Public */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />

      {/* Terms acceptance — must accept before using platform */}
      <Route path="/accept-terms" element={<TermsAcceptancePage />} />

      {/* Email verification & password reset — Issue #2 & #3 */}
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Auth */}
      <Route path="/dashboard" element={<DashboardPage />} />

      {/* Onboarding */}
      <Route path="/onboarding/profile" element={<ProfileOnboarding />} />
      {/* Legacy instrument page (raw scores, retired June 2026) — old bookmarks
          and links land on the live V1 Portrait instead */}
      <Route path="/onboarding/psychometric" element={<Navigate to="/onboarding/psychometric/v1" replace />} />
      <Route path="/onboarding/psychometric/v1" element={<PsychometricOnboardingV1 />} />

      {/* Design lab (Sep 2026): alternate dashboard + one-at-a-time Portrait,
          reachable only with an admin session; everyone else lands on the live pages. */}
      <Route path="/lab/dashboard" element={<LabDashboard />} />
      <Route path="/lab/portrait" element={<PsychometricOnboardingV1 lab />} />

      {/* Core app — Step 1 */}
      <Route path="/matches" element={<MatchesPage />} />
      <Route path="/interests" element={<InterestsPage />} />
      <Route path="/messages" element={<MessagesPage />} />
      <Route path="/messages/:otherUserId" element={<ChatPage />} />
      <Route path="/profile" element={<ProfileViewPage />} />
      <Route path="/profile/:userId" element={<ProfileViewPage />} />
      <Route path="/couple-portrait/:userId" element={<CouplePortraitPage />} />

      {/* KYC Verification */}
      <Route path="/kyc-verification" element={<KYCVerificationPage />} />

      {/* Monetization */}
      <Route path="/boost" element={<BoostPage />} />
      <Route path="/subscription" element={<SubscriptionPage />} />
      <Route path="/subscription/success" element={<PaymentSuccessPage />} />
      <Route path="/refer" element={<ReferPage />} />

      {/* Deep Exploration (couple 108Q) */}
      <Route path="/deep/report/:pairId" element={<CompatibilityIntelligenceReport />} />
      <Route path="/deep/questionnaire" element={<DeepQuestionnaireFlow />} />
      <Route path="/deep/demo-report" element={<DemoDeepReport />} />

      {/* V1 Reports — the only report lineup (legacy two-tier funnel removed July 2026) */}
      <Route path="/report/relationship-intelligence" element={<RelationshipIntelligenceReport />} />
      <Route path="/report/marriage-readiness" element={<MarriageReadinessReportPage />} />
      <Route path="/sample/relationship-intelligence" element={<SampleReportPage kind="relationship-intelligence" />} />
      <Route path="/sample/marriage-readiness" element={<SampleReportPage kind="marriage-readiness" />} />
      <Route path="/report/couple-compatibility/:partnerId" element={<CoupleCompatibilityReport />} />
      <Route path="/assessment/unlock"       element={<AssessmentUnlock />} />

      {/* 108Q Insights flow — RETIRED. All /insights/* paths redirect to V2 */}
      <Route path="/insights/*"  element={<Navigate to="/couple" replace />} />
      <Route path="/insights"    element={<Navigate to="/couple" replace />} />
      <Route path="/couple"      element={<CouplesLandingPage />} />
      <Route path="/couple/begin" element={<CoupleBeginPage />} />

      {/* New Design V2 — Couple Compatibility Flow (link-based, no login) */}
      <Route path="/v2/me" element={<V2SinglesStart />} />
      <Route path="/v2/single" element={<V2SingleFlow />} />
      {/* Verify a printed Couple Compatibility Report (reference + code on its cover) */}
      <Route path="/verify" element={<VerifyReportPage />} />
      <Route path="/quiz" element={<QuizPage />} />
      {/* Friendly share-card URL — portrait share links use /portrait?ref=CODE */}
      <Route path="/portrait" element={<V2SinglesStart />} />
      {/* Family-managed profiles: the individual's own private Portrait link */}
      <Route path="/portrait/invite/:token" element={<PortraitInvite />} />
      <Route path="/v2/couple" element={<V2EntryGate />} />
      <Route path="/v2/couple/preview" element={<V2CouplePreview />} />
      <Route path="/v2/couple/:linkToken" element={<V2CoupleFlow />} />

      {/* V2 Individual-First Funnel (Solo-First Pivot) — flag V2_INDIVIDUAL_ENABLED */}
      <Route path="/v2/individual" element={<V2IndividualGate />} />
      <Route path="/v2/invite/:inviteToken" element={<V2PartnerInvite />} />
      <Route path="/v2/family/:ptoken" element={<FamilyFormPage />} />

      {/* Book store — public page, free sample reader, token-gated eBook reader */}
      <Route path="/book" element={<BookPage />} />
      <Route path="/book/sample" element={<BookReaderPage />} />
      <Route path="/book/read/:token" element={<BookReaderPage />} />

      {/* Notification settings & unsubscribe */}
      <Route path="/notification-preferences" element={<NotificationPreferencesPage />} />
      <Route path="/unsubscribe" element={<UnsubscribePage />} />

      {/* Public marketing pages */}
      <Route path="/how-it-works" element={<HowItWorksPage />} />
      <Route path="/concierge" element={<ConciergePage />} />
      <Route path="/privacy-concierge" element={<PrivacyConciergeLanding />} />
      <Route path="/pay/concierge/:token" element={<ConciergePayPage />} />
      {/* Alliance Member portal (founder, Oct 16 2026: /alliance). The old /partner address keeps working for bookmarks and earlier WhatsApp links. */}
      <Route path="/alliance" element={<PartnerPortal />} />
      <Route path="/partner" element={<Navigate to="/alliance" replace />} />
      <Route path="/alliance-members" element={<AllianceMembersPage />} />
      <Route path="/alliance-members/terms" element={<AllianceTermsPage />} />
      <Route path="/about" element={<AboutPage />} />
      {/* Retired Oct 2026: /care is the one Care entry point. */}
      <Route path="/wellness"   element={<Navigate to="/care" replace />} />
      <Route path="/ecosystem"  element={<EcosystemPage />} />
      <Route path="/care"       element={<CarePage />} />
      {/* Care Phase 2: the expert portal, behind CARE_MARKETPLACE_ENABLED. */}
      <Route path="/care/expert" element={<CareExpertPortal />} />
      {/* Care Phase 3: directory and booking, behind CARE_MARKETPLACE_ENABLED. */}
      <Route path="/care/experts" element={<CareExperts />} />
      <Route path="/care/experts/:expertId" element={<CareExpertBooking />} />
      <Route path="/care/booking/:bookingId" element={<CareBooking />} />
      <Route path="/care/my" element={<CareMy />} />
      <Route path="/methodology" element={<MethodologyPage />} />
      <Route path="/intentional-matchmaking" element={<IntentionalMatchmakingPage />} />
      <Route path="/relationship-intelligence" element={<RelationshipIntelligencePage />} />

      {/* Support pages */}
      <Route path="/help" element={<HelpCenterPage />} />
      <Route path="/safety-tips" element={<SafetyTipsPage />} />
      <Route path="/contact" element={<ContactUsPage />} />
      <Route path="/blogs" element={<BlogListPage />} />
      <Route path="/blogs/:slug" element={<BlogPostPage />} />
      <Route path="/faq" element={<FAQPage />} />

      {/* Legal — Step 2 */}
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/cancellation-policy" element={<CancellationPolicyPage />} />
      <Route path="/delete-account" element={<DeleteAccountPage />} />
      <Route path="/about-legal" element={<AboutUsPage />} />

      {/* Super-admin diagnostic view-as entry (token in URL hash, per-tab) */}
      <Route path="/diag/start" element={<DiagnosticStart />} />

      {/* Admin */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="demographics" element={<AdminDemographics />} />
        <Route path="profiles" element={<AdminProfiles />} />
        <Route path="subscriptions" element={<AdminSubscriptions />} />
        <Route path="deep" element={<AdminDeep />} />
        <Route path="couple-reports" element={<AdminCoupleReports />} />
        <Route path="reports" element={<AdminReports />} />
        <Route path="analytics" element={<AdminAnalytics />} />
        <Route path="match-diagnostics" element={<AdminMatchDiagnostics />} />
        <Route path="growth-funnel" element={<AdminGrowthFunnel />} />
        <Route path="leads" element={<AdminLeads />} />
        <Route path="concierge" element={<AdminConcierge />} />
        <Route path="concierge/:caseId" element={<AdminConciergeClient />} />
        <Route path="book-orders" element={<AdminBookOrders />} />
        <Route path="accounts" element={<AdminAccounts />} />
        <Route path="pricing" element={<AdminPricing />} />
        <Route path="blog" element={<AdminBlog />} />
        <Route path="blog/new" element={<AdminBlogEditor />} />
        <Route path="blog/:postId/edit" element={<AdminBlogEditor />} />
        <Route path="kyc" element={<AdminKYC />} />
        <Route path="feedback" element={<AdminFeedback />} />
        <Route path="moderation" element={<AdminModeration />} />
        <Route path="team" element={<AdminTeam />} />
        <Route path="support" element={<AdminSupport />} />
        <Route path="whatsapp-replies" element={<AdminWhatsAppInbox />} />
        <Route path="promo" element={<AdminPromo />} />
        <Route path="access-codes" element={<AdminAccessCodes />} />
        <Route path="partners" element={<AdminPartners />} />
        <Route path="care" element={<AdminCare />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </Suspense>
    </>
  );
}

function App() {
  return (
    <div className="App overflow-x-hidden">
      <a href="#main" className="ss-skip">Skip to content</a>
      <BrowserRouter>
        <SessionBootstrap />
        <ReferralCapture />
        <DiagnosticBanner />
        <AppRouter />
        <Toaster
          position="top-right"
          richColors
          duration={3000}
          theme="dark"
          toastOptions={{
            style: { background: '#0b1729', border: '1px solid #204377', color: '#fff9ea', borderRadius: 12 },
          }}
        />
        {/* Floating widgets download after the page itself — nothing to show
            while their chunks load, so the fallback is empty. */}
        <Suspense fallback={null}>
          <InstallPrompt />
          {/* BookPromoPopup retired Sep 2026 — 87% dismissal, zero buy clicks.
              Component kept dormant; the book gets its own sales funnel under
              the SS Institute vertical instead of interrupting members. */}
          <SathiWidgetGate />
        </Suspense>
      </BrowserRouter>
    </div>
  );
}

export default App;
